/**
 * Configuración centralizada de PrestaShop.
 *
 * Todos los identificadores (idioma, moneda, país, tienda, categorías especiales)
 * se leen desde variables de entorno para no depender de valores hardcodeados.
 */

/**
 * Convierte una variable de entorno a número entero, usando el fallback
 * cuando la variable no está definida o no es un número válido.
 */
function toId(value: string | undefined, fallback: number): number {
  const parsed = parseInt(value ?? '', 10);
  return Number.isNaN(parsed) ? fallback : parsed;
}

export const PRESTASHOP_CONFIG = {
  BASE_URL: process.env.PRESTASHOP_BASE_URL || '',
  WS_KEY: process.env.PRESTASHOP_WS_KEY || '',
  SHOP_ID: toId(process.env.NEXT_PUBLIC_PRESTASHOP_SHOP_ID, 1),
  COUNTRY_ID: toId(process.env.NEXT_PUBLIC_PRESTASHOP_COUNTRY_ID, 6),
  LANGUAGE_ID: toId(process.env.NEXT_PUBLIC_PRESTASHOP_LANGUAGE_ID, 1),
  CURRENCY_ID: toId(process.env.NEXT_PUBLIC_PRESTASHOP_CURRENCY_ID, 1),
  NOVEDADES_ID: toId(process.env.NEXT_PUBLIC_PRESTASHOP_NOVEDADES_ID, 64),
  PROMOCIONES_ID: toId(process.env.NEXT_PUBLIC_PRESTASHOP_PROMOCIONES_ID, 67),
  GLUTEN_FREE_ID: toId(process.env.NEXT_PUBLIC_PRESTASHOP_GLUTEN_FREE_ID, 65),
  BIO_ID: toId(process.env.NEXT_PUBLIC_PRESTASHOP_BIO_ID, 68),
  SUGAR_FREE_ID: toId(process.env.NEXT_PUBLIC_PRESTASHOP_SUGAR_FREE_ID, 69),
  PRESENTATION_FEATURE_ID: toId(process.env.NEXT_PUBLIC_PRESTASHOP_PRESENTATION_FEATURE_ID, 4),
} as const;

/**
 * Mapa de código ISO de idioma -> id_lang de PrestaShop.
 *
 * Las referencias a `process.env.NEXT_PUBLIC_*` deben ser estáticas para que
 * Next.js las pueda inlinear en el bundle del cliente, por eso el mapa es
 * explícito en lugar de construirse dinámicamente.
 */
export const PRESTASHOP_LANGUAGE_IDS: Readonly<Record<string, number>> = {
  es: toId(process.env.NEXT_PUBLIC_PRESTASHOP_LANGUAGE_ID_ES, PRESTASHOP_CONFIG.LANGUAGE_ID),
  en: toId(process.env.NEXT_PUBLIC_PRESTASHOP_LANGUAGE_ID_EN, 2),
};

/**
 * Devuelve el id_lang de PrestaShop para un código de idioma.
 * Si el idioma no está mapeado, devuelve el idioma por defecto de la tienda.
 */
export function getPrestashopLanguageId(lang?: string | null): number {
  if (!lang) {
    return PRESTASHOP_CONFIG.LANGUAGE_ID;
  }

  return PRESTASHOP_LANGUAGE_IDS[lang.toLowerCase()] ?? PRESTASHOP_CONFIG.LANGUAGE_ID;
}

/**
 * Devuelve el id_currency de PrestaShop configurado para la tienda.
 */
export function getPrestashopCurrencyId(): number {
  return PRESTASHOP_CONFIG.CURRENCY_ID;
}
